import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faEdit,
  faTrash,
  faRotateLeft,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { config } from '../../data/config';
import { usersAPI } from './actions';
import Modal from './Modal';
import AddEditUser from './AddEditUser';
import './StaffManagement.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [kindergartens, setKindergartens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);

  // Фильтры
  const [filters, setFilters] = useState({
    role: 'all',
    district: -1
  });
  
  // Модальное окно
  const [modal, setModal] = useState({
    isOpen: false,
    action: 'add',
    userData: null,
    errorFields: []
  });

  const validUser = (roles, person) => {
    return true;
  };

  // Колонки таблицы
  const columns = [
    {
      key: 'index',
      label: '№',
      sortable: false,
      width: '5%'
    },
    {
      key: 'area',
      label: 'Заклад/район',
      sortable: true,
      width: '25%'
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      width: '20%'
    },
    {
      key: 'userInfo',
      label: 'Користувач',
      sortable: true,
      width: '25%'
    },
    {
      key: 'authType',
      label: 'Тип авторизації',
      sortable: true,
      width: '15%'
    },
    {
      key: 'actions',
      label: 'Дії',
      sortable: false,
      width: '10%'
    }
  ];

  useEffect(() => {
    if (!validUser(['admin', 'superadmin'])) {
      setError("An attempt by the user opening the page is not validated");
      window.location.href = "#/";
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, kgRes] = await Promise.all([
        usersAPI.getUsers(),
        usersAPI.getKindergartens()
      ]);

      if (usersRes.error || kgRes.error) {
        setError(usersRes.error?.message || kgRes.error?.message);
        return;
      }

      let usersList = usersRes.data.filter(item => !!item.area);
      const kgList = kgRes.data;

      // Обогащаем данные пользователей информацией о детских садах
      usersList = usersList.map(item => {
        if (!item.area.match(/^[a-z0-9]{24}$/i)) return item;
        
        const kindergarten = kgList.find(kg => kg._id === item.area);
        return kindergarten ? { ...kindergarten, ...item } : item;
      });

      usersList.sort((a, b) => a.area.localeCompare(b.area));
      setUsers(usersList);
      setKindergartens(kgList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация пользователей по табу и фильтрам
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Фильтр по активности (таб)
      if (activeTab === 'active' && !user.isActive) return false;
      if (activeTab === 'inactive' && user.isActive) return false;
      
      // Фильтр по роли
      if (filters.role !== 'all' && user.role !== filters.role) return false;
      
      // Фильтр по району
      if (filters.district !== -1) {
        const districtName = config.districts[filters.district];
        if (!districtName || !user.district?.startsWith(districtName.slice(0, 2))) {
          return false;
        }
      }
      
      return true;
    });
  }, [users, activeTab, filters]);

  // Сортировка
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    
    return [...filteredUsers].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'area':
          aValue = a.area || '';
          bValue = b.area || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'userInfo':
          aValue = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
          bValue = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
          break;
        case 'authType':
          aValue = config.authType[a.privelegesAuthType] || '';
          bValue = config.authType[b.privelegesAuthType] || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const handleSort = (key) => {
    if (!columns.find(col => col.key === key)?.sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = (actionId, user) => {
    if (actionId === 'edit') {
      setModal({
        isOpen: true,
        action: 'edit',
        userData: user,
        errorFields: []
      });
    } else if (actionId === 'delete' || actionId === 'restore') {
      handleUserStatusChange(user, actionId === 'restore');
    }
    setActiveMenuIndex(null);
  };

  const handleUserStatusChange = async (user, activate = false) => {
    try {
      const updatedUser = { ...user, isActive: activate };
      const result = await usersAPI.updateUser(user._id, updatedUser);
      
      if (result.error) {
        alert('Помилка: ' + result.error.message);
        return;
      }

      alert(`Користувач успішно ${activate ? 'активований' : 'деактивований'}`);
      loadData();
    } catch (err) {
      alert('Помилка: ' + err.message);
    }
  };

  const openAddModal = () => {
    setModal({
      isOpen: true,
      action: 'add',
      userData: null,
      errorFields: []
    });
  };

  const closeModal = () => {
    setModal({ isOpen: false, action: 'add', userData: null, errorFields: [] });
  };

  const handleSaveUser = async (userData) => {
    const requiredFields = ['firstName', 'email', 'role', 'area', 'grant', 'sex'];
    const errors = requiredFields.filter(field => !userData[field]);
    
    if (errors.length > 0) {
      setModal(prev => ({ ...prev, errorFields: errors }));
      return;
    }

    try {
      const cleanData = { ...userData };
      delete cleanData.id;

      let result;
      if (modal.action === 'add') {
        result = await usersAPI.addUser(cleanData);
      } else {
        result = await usersAPI.updateUser(userData._id, cleanData);
      }

      if (result.error) {
        alert('Помилка: ' + result.error.message);
        return;
      }

      alert(modal.action === 'add' ? 'Користувач успішно доданий' : 'Користувач успішно змінений');
      closeModal();
      loadData();
    } catch (err) {
      alert('Помилка: ' + err.message);
    }
  };

  const copyToExcel = () => {
    const activeUsers = users.filter(user => user.isActive);
    const headers = ['ПІБ', 'email', 'Роль'].join('\t');
    const csv = [headers, ...activeUsers.map(user => 
      [user.firstName, user.email, config.rolesName[config.roles.indexOf(user.role)]]
        .map(el => el?.replace(/[\n\t]+/g, ' ') || '')
        .join('\t')
    )].join('\n');

    navigator.clipboard.writeText(csv).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = csv;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  };

  const GetIndexRole = ({ idRole, firstName, lastName }) => {
    const index = config.roles.indexOf(idRole);
    return (
      <span>
        {config.rolesName[index]}: {firstName} {lastName}
      </span>
    );
  };

  const GetInstitutionInfo = ({ user }) => {
    if (user.role === config.roles[0] || user.role === config.roles[3] || user.role === config.roles[4]) {
      return (
        <a href={`/#/objpage/${user.area}`} target="_blank" rel="noopener noreferrer">
          {user.name ? (user.name.length > 60 ? user.name.slice(0, 60) + '...' : user.name) : ''} 
          {user.number ? ` №${user.number}` : ' б/н'}
        </a>
      );
    }
    return <span>{user.area}</span>;
  };

  const ActionsMenu = ({ onActionSelect, onClose, user, isActiveTab }) => {
    const actions = isActiveTab ? [
      {
        id: 'edit',
        label: 'Редагувати',
        icon: faEdit
      },
      {
        id: 'delete',
        label: 'Видалити',
        icon: faTrash
      }
    ] : [
      {
        id: 'restore',
        label: 'Поновити',
        icon: faRotateLeft
      }
    ];

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, user)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="staff-management">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (loading) {
    return <div className="staff-management loading">Завантаження...</div>;
  }

  return (
    <div className="staff-management">
      <main className="staff-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <div className="staff-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="staff-label">Статус користувачів</span>
                <ul className="staff-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Активні
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'inactive' ? 'active' : ''}`}
                      onClick={() => setActiveTab('inactive')}
                    >
                      Деактивовані
                    </button>
                  </li>
                </ul>
              </div>
              <div className="queuetable-switch-wrap">
                <FilterUsers 
                  filters={filters}
                  onFilterChange={(newFilters) => setFilters(newFilters)}
                />
                {validUser(['superadmin']) && (
                  <button onClick={copyToExcel} className="btn btn-sm btn-outline-primary ms-2">
                    Скопіювати для Excel
                  </button>
                )}
                <button onClick={openAddModal} className="btn btn-sm btn-primary ms-2">
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Додати користувача
                </button>
              </div>
            </div>

            {/* Таблица */}
            <div className="card staff-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table staff-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th 
                            key={column.key}
                            style={{ width: column.width, cursor: column.sortable ? 'pointer' : 'default' }}
                            onClick={() => handleSort(column.key)}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon 
                                  icon={getSortIcon(column.key)} 
                                  className="ms-1" 
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((user, index) => (
                        <tr key={user._id}>
                          <td>{index + 1}</td>
                          <td>
                            <GetInstitutionInfo user={user} />
                          </td>
                          <td>
                            <span className={user.isActive ? '' : 'text-muted'}>
                              {user.email}
                              {!user.isActive && ' (неактивний)'}
                            </span>
                          </td>
                          <td>
                            <GetIndexRole 
                              idRole={user.role} 
                              firstName={user.firstName} 
                              lastName={user.lastName} 
                            />
                          </td>
                          <td>
                            {config.authType[user.privelegesAuthType] || config.authType.none}
                          </td>
                          <td className="queuetable-actions text-end">
                            <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                              <button
                                className="btn btn-sm btn-link text-decoration-none p-0"
                                onClick={() => handleMenuToggle(index)}
                                style={{ color: '#0d6efd' }}
                              >
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                              </button>
                              
                              {activeMenuIndex === index && (
                                <ActionsMenu 
                                  onActionSelect={handleActionSelect}
                                  onClose={handleMenuClose}
                                  user={user}
                                  isActiveTab={activeTab === 'active'}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {sortedUsers.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">Користувачів не знайдено</p>
                    </div>
                  )}
                </div>

                {/* Пагинация */}
                <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    Всього {sortedUsers.length} користувачів
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.action === 'add' ? 'Додати користувача' : 'Змінити користувача'}
      >
        <AddEditUser
          action={modal.action}
          data={modal.userData}
          onSave={handleSaveUser}
          onClose={closeModal}
          errorFields={modal.errorFields}
        />
      </Modal>
    </div>
  );
};

const FilterUsers = ({ filters, onFilterChange }) => {
  const handleChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <div className="filter-group">
        <select 
          value={filters.role} 
          onChange={(e) => handleChange('role', e.target.value)}
          className="form-select form-select-sm"
        >
          <option value="all">Всі ролі</option>
          {config.roles.map((role, index) => (
            <option key={role} value={role}>{config.rolesName[index]}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <select 
          value={filters.district} 
          onChange={(e) => handleChange('district', parseInt(e.target.value))}
          className="form-select form-select-sm"
        >
          <option value={-1}>Всі райони</option>
          {config.districts.map((district, index) => (
            <option key={district} value={index}>{district}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default Users;